import React, { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router";
import { Box, Button, DialogActions, DialogContent, DialogTitle, IconButton, Link, Stack, Typography, Tooltip, FormControl, OutlinedInput, FormHelperText, CircularProgress, Backdrop } from "@mui/material";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import InfoRoundedIcon from "@mui/icons-material/InfoRounded";
import OpenInNewRoundedIcon from "@mui/icons-material/OpenInNewRounded";
import { useCreateStarterBlockModelMutation } from "../../../../../../shell/services/instance";
import { ContentModel, ContentModelField } from "../../../../../../shell/services/types";
import { Field } from "../Field";
import { OG_IMAGE_FIELD, StarterBlockProps } from "./configs";
type TextInputFieldProps = {
  label: string;
  name: string;
  placeholder?: string;
  required?: boolean;
  error?: string;
  value?: string;
  toolTip?: string;
  onChange?: (val: string) => void;
};
type StarterBlockFormProps = {
  block: StarterBlockProps;
  onClose: () => void;
  setActiveStep: (step: "selection" | "form") => void;
};
type CreateStarterBlockFieldsProps = Omit<ContentModelField, "ZUID" | "contentModelZUID" | "datatypeOptions" | "createdAt" | "updatedAt" | "deletedAt">;
const TextInputField: React.FC<TextInputFieldProps> = ({
  label = "Label",
  name,
  placeholder = "Text",
  required = false,
  error = "",
  value,
  toolTip,
  onChange,
  ...other
}) => {
  function handleChage(e: React.ChangeEvent<HTMLInputElement>) {
    const val = e.target.value;
    onChange(val);
  }
  return <Box width="100%" data-sentry-element="Box" data-sentry-component="TextInputField" data-sentry-source-file="StarterBlockForm.tsx">
      <FormControl variant="standard" fullWidth data-sentry-element="FormControl" data-sentry-source-file="StarterBlockForm.tsx">
        <Typography variant="body2" color="text.primary" fontWeight={600} mb={0.25} display="flex" flexDirection="row" justifyContent="flex-start" alignItems="baseline" data-sentry-element="Typography" data-sentry-source-file="StarterBlockForm.tsx">
          {label}
          {required && <Tooltip placement="top" title={toolTip}>
              <InfoRoundedIcon sx={{
            width: 14,
            height: 14,
            color: "action.active",
            ml: 0.75
          }} />
            </Tooltip>}
        </Typography>
        <OutlinedInput {...other} fullWidth placeholder={placeholder} required={required} error={!!error} inputProps={{
        name: name,
        value: value,
        onInput: handleChage
        // "data-cy": `form-input-${name}`,
      }} data-sentry-element="OutlinedInput" data-sentry-source-file="StarterBlockForm.tsx" />
        <FormHelperText error={!!error} variant="standard" data-cy={`starter-block-form-${name}-error`} data-sentry-element="FormHelperText" data-sentry-source-file="StarterBlockForm.tsx">
          {error}
        </FormHelperText>
      </FormControl>
    </Box>;
};
export const StarterBlockForm: React.FC<StarterBlockFormProps> = ({
  block,
  onClose,
  setActiveStep
}) => {
  const {
    t
  } = useTranslation();
  const history = useHistory();
  const [isLoading, setIsLoading] = useState(false);
  const [blockModelData, setBlockModelData] = useState({
    ...block
  });
  const [error, setError] = useState<Record<string, string>>({
    label: "",
    name: ""
  });
  const [createStarterBlock] = useCreateStarterBlockModelMutation();
  function cleanString(str: string) {
    return str.toLowerCase().replace(/\W/g, "_");
  }
  function parseErrorMessage(str: string) {
    return str.split(":")[2] || str;
  }
  const handleBlockLabelChange = (val: string) => {
    setBlockModelData((prev: any) => ({
      ...prev,
      label: val,
      name: cleanString(val)
    }));
  };
  const handleBlockNameChange = (val: string) => {
    setBlockModelData((prev: any) => ({
      ...prev,
      name: cleanString(val)
    }));
  };
  const handleFormSubmit = useCallback(async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!blockModelData?.label || !blockModelData?.name) return;
    setError({
      label: "",
      name: ""
    });
    setIsLoading(true);
    try {
      const formData = new FormData(e.currentTarget);
      const {
        label,
        name
      } = Object.fromEntries(formData) as {
        label: string;
        name: string;
      };
      const createBlockModelPayload: Partial<ContentModel> = {
        label,
        name,
        type: "block",
        description: block?.description,
        parentZUID: "",
        listed: true
      };
      const fieldsPayload: CreateStarterBlockFieldsProps[] = [...blockModelData?.fields, OG_IMAGE_FIELD]?.map((field, index) => {
        return {
          name: field?.name,
          label: field?.label,
          description: field?.description,
          datatype: field?.datatype,
          sort: index + 1,
          settings: {
            ...field?.settings,
            list: field?.name === "og_image" ? false : true
          }
        };
      });
      const createStarterBlockRes: any = await createStarterBlock({
        modelData: createBlockModelPayload,
        fields: fieldsPayload,
        code: block?.code
      });
      if (createStarterBlockRes?.error) {
        const errorMessage = parseErrorMessage(createStarterBlockRes?.error?.error);
        setError({
          name: errorMessage.includes("name") ? t("schema.starterBlockFormReferenceIdInUse") : errorMessage,
          label: errorMessage.includes("label") || errorMessage.includes("name") && cleanString(label) === name ? t("schema.starterBlockFormDisplayNameInUse") : ""
        });
        throw new Error(errorMessage);
      } else {
        const ZUID = createStarterBlockRes?.data?.model?.ZUID;
        setIsLoading(false);
        onClose();
        history.push(`/blocks/${ZUID}`);
      }
    } catch (err) {
      setIsLoading(false);
      console.error("Error during form submission:", err);
    }
  }, [blockModelData, createStarterBlock, onClose, history, t]);
  return <Box component="form" maxHeight="100%" width="100%" display="flex" flexDirection="column" justifyContent="space-between" alignItems="stretch" overflow="hidden" onSubmit={handleFormSubmit} data-cy="starter-block-form" data-sentry-element="Box" data-sentry-component="StarterBlockForm" data-sentry-source-file="StarterBlockForm.tsx">
      <DialogTitle component="div" data-sentry-element="DialogTitle" data-sentry-source-file="StarterBlockForm.tsx">
        <Stack direction="row" justifyContent="space-between" alignItems="flex-start" data-sentry-element="Stack" data-sentry-source-file="StarterBlockForm.tsx">
          <Box width="90%" data-sentry-element="Box" data-sentry-source-file="StarterBlockForm.tsx">
            <Typography variant="h5" fontWeight={700} data-cy="starter-block-form-title" data-sentry-element="Typography" data-sentry-source-file="StarterBlockForm.tsx">
              {block?.label}
            </Typography>
          </Box>
          <IconButton size="small" onClick={() => onClose()} data-sentry-element="IconButton" data-sentry-source-file="StarterBlockForm.tsx">
            <CloseRoundedIcon fontSize="small" data-sentry-element="CloseRoundedIcon" data-sentry-source-file="StarterBlockForm.tsx" />
          </IconButton>
        </Stack>
      </DialogTitle>
      <DialogContent sx={{
      p: 0,
      backgroundColor: "grey.50",
      height: "100%",
      overflowY: "auto",
      overflowX: "hidden",
      maxHeight: "100%",
      "&::-webkit-scrollbar-thumb, & *::-webkit-scrollbar-thumb": {
        backgroundColor: "grey.300",
        borderRadius: "4px"
      }
    }} dividers data-sentry-element="DialogContent" data-sentry-source-file="StarterBlockForm.tsx">
        <Box display="flex" flexDirection="column" rowGap={2} data-sentry-element="Box" data-sentry-source-file="StarterBlockForm.tsx">
          <Box bgcolor="grey.100" height="320px" width="100%" display="flex" justifyContent="center" alignItems="center" position="relative" overflow="hidden" boxSizing="border-box" borderBottom="8px solid" borderTop="8px solid" borderColor="grey.100" data-sentry-element="Box" data-sentry-source-file="StarterBlockForm.tsx">
            <img data-cy="starter-block-form-image" loading="lazy" src={block?.image} alt={block?.label} style={{
            maxWidth: blockModelData?.name === "hero_image_below" ? "50%" : "60%"
          }} />
          </Box>
          <Box display="flex" flexDirection="row" justifyContent="space-between" alignItems="flex-start" columnGap="20px" px={3} sx={{
          boxSizing: "border-box",
          minHeight: "100px"
        }} data-sentry-element="Box" data-sentry-source-file="StarterBlockForm.tsx">
            <Box flexGrow={1} data-sentry-element="Box" data-sentry-source-file="StarterBlockForm.tsx">
              <Typography variant="h6" fontWeight={700} data-sentry-element="Typography" data-sentry-source-file="StarterBlockForm.tsx">
                {t("schema.starterBlockFormDetails")}
              </Typography>
              <Box display="flex" flexDirection="column" justifyContent="flex-start" alignItems="flex-start" rowGap="12px" pt="8px" pb="20px" data-sentry-element="Box" data-sentry-source-file="StarterBlockForm.tsx">
                <TextInputField label={t("schema.starterBlockFormDisplayName")} name="label" placeholder={t("schema.starterBlockFormDisplayNamePlaceholder")} required value={blockModelData?.label} onChange={handleBlockLabelChange} toolTip={t("schema.starterBlockFormDisplayNameTooltip")} error={error["label"] || ""} data-cy="starter-block-form-label" data-sentry-element="TextInputField" data-sentry-source-file="StarterBlockForm.tsx" />
                <TextInputField label={t("schema.starterBlockFormReferenceId")} name="name" placeholder={t("schema.starterBlockFormReferenceIdPlaceholder")} required value={blockModelData?.name} toolTip={t("schema.starterBlockFormReferenceIdTooltip")} onChange={handleBlockNameChange} error={error["name"] || ""} data-cy="starter-block-form-name" data-sentry-element="TextInputField" data-sentry-source-file="StarterBlockForm.tsx" />
              </Box>
              {!!blockModelData?.fields?.length && <>
                  <Typography variant="h6" fontWeight={700}>
                    {t("schema.starterBlockFormFields")}
                  </Typography>
                  <Box display="flex" flexDirection="column" justifyContent="flex-start" alignItems="stretch" rowGap="12px" pt="8px" pb="8px" width="100%" data-cy="starter-block-form-fields-container">
                    {block?.fields?.map((field: any, index: number) => <Field key={field?.name} withMenu={false} withHover={false} withDragIcon={false} index={index} disableDrag field={field} />)}
                  </Box>
                </>}
            </Box>
            <Box width="320px" flexGrow={0} data-sentry-element="Box" data-sentry-source-file="StarterBlockForm.tsx">
              <Typography variant="h6" fontWeight={700} data-sentry-element="Typography" data-sentry-source-file="StarterBlockForm.tsx">
                {t("schema.starterBlockFormResources")}
              </Typography>
              <Box display="flex" flexDirection="column" justifyContent="flex-start" alignItems="flex-start" rowGap={1} pt={1.5} pb={2.5} data-sentry-element="Box" data-sentry-source-file="StarterBlockForm.tsx">
                <Link data-cy="starter-block-form-preview-link" href={block?.previewLink} target="_blank" color="primary.main" variant="body2" display="flex" flexDirection="row" alignItems="center" columnGap={1} sx={{
                textDecorationColor: theme => theme.palette.primary.main,
                "&:hover": {
                  textDecorationColor: theme => theme.palette.primary.main
                }
              }} data-sentry-element="Link" data-sentry-source-file="StarterBlockForm.tsx">
                  {t("common.preview")}
                  <OpenInNewRoundedIcon fontSize="small" data-sentry-element="OpenInNewRoundedIcon" data-sentry-source-file="StarterBlockForm.tsx" />
                </Link>
                <Link data-cy="starter-block-form-code-template-link" href={block?.codeTemplateLink} target="_blank" color="primary.main" variant="body2" display="flex" flexDirection="row" alignItems="center" columnGap={1} sx={{
                textDecorationColor: theme => theme.palette.primary.main,
                "&:hover": {
                  textDecorationColor: theme => theme.palette.primary.main
                }
              }} data-sentry-element="Link" data-sentry-source-file="StarterBlockForm.tsx">
                  {t("schema.starterBlockFormCodeTemplate")}
                  <OpenInNewRoundedIcon fontSize="small" data-sentry-element="OpenInNewRoundedIcon" data-sentry-source-file="StarterBlockForm.tsx" />
                </Link>
              </Box>
              <Typography variant="h6" fontWeight={700} data-sentry-element="Typography" data-sentry-source-file="StarterBlockForm.tsx">
                {t("schema.starterBlockFormDescription")}
              </Typography>
              <Box display="flex" flexDirection="column" justifyContent="flex-start" alignItems="flex-start" rowGap={1} pt={1.5} pb={2.5} data-sentry-element="Box" data-sentry-source-file="StarterBlockForm.tsx">
                <Typography variant="body2" color="text.secondary" data-cy="starter-block-form-description" data-sentry-element="Typography" data-sentry-source-file="StarterBlockForm.tsx">
                  {block?.description}
                </Typography>
                <Link variant="body2" href={block?.codeReference} target="_blank" underline="always" color="info.main" mt={2} data-cy="starter-block-form-code-reference-link" data-sentry-element="Link" data-sentry-source-file="StarterBlockForm.tsx">
                  {t("schema.starterBlockFormSeeBootstrapTemplate")}
                </Link>
              </Box>
            </Box>
          </Box>
        </Box>
      </DialogContent>
      <DialogActions sx={{
      pt: 2.5
    }} data-sentry-element="DialogActions" data-sentry-source-file="StarterBlockForm.tsx">
        <Button variant="outlined" color="inherit" onClick={() => setActiveStep("selection")} data-sentry-element="Button" data-sentry-source-file="StarterBlockForm.tsx">
          {t("common.back")}
        </Button>
        <Button variant="contained" data-cy="starter-block-form-submit" type="submit" disabled={!blockModelData?.label || !blockModelData?.name || !!isLoading} data-sentry-element="Button" data-sentry-source-file="StarterBlockForm.tsx">
          {t("schema.starterBlockFormCreateBlockModel")}
        </Button>
      </DialogActions>

      <Backdrop data-cy="starter-block-form-loading-backdrop" open={isLoading} sx={{
      position: "absolute",
      backgroundColor: "background.paper",
      justifyContent: "center",
      alignItems: "center"
    }} data-sentry-element="Backdrop" data-sentry-source-file="StarterBlockForm.tsx">
        <Box display="flex" flexDirection="column" justifyContent="center" alignItems="center" rowGap={1} data-sentry-element="Box" data-sentry-source-file="StarterBlockForm.tsx">
          <CircularProgress color="primary" size={60} sx={{
          p: 1
        }} data-sentry-element="CircularProgress" data-sentry-source-file="StarterBlockForm.tsx" />
          <Typography variant="h5" color="text.primary" fontWeight={700} data-sentry-element="Typography" data-sentry-source-file="StarterBlockForm.tsx">
            {t("schema.starterBlockFormCreatingModel")}
          </Typography>
          <Typography variant="body2" color="text.secondary" data-sentry-element="Typography" data-sentry-source-file="StarterBlockForm.tsx">
            {t("schema.starterBlockFormCreatingModelDescription")}
          </Typography>
        </Box>
      </Backdrop>
    </Box>;
};